import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Volume2, X, BookOpen, List, RefreshCw, Square, Languages, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { textToSpeechService } from '../services/textToSpeechService';
import { RichDictionaryItem, ResolvedWordTranslation } from '../services/richDictionaryService';
import { translationService } from '../services/translationService';

export interface UnifiedWordTarget {
  word: string;
  sourceLang: string;
  targetLang: string;
  x?: number;
  y?: number;
  containerRect?: DOMRect | { left: number; top: number; width: number; height: number };
}

export interface UnifiedDictionaryModalProps {
  resolvedData: ResolvedWordTranslation | null;
  loading: boolean;
  target: UnifiedWordTarget | null;
  onClose: () => void;
  isOpenExpandedModal?: boolean;
  onOpenExpandedModal?: () => void;
  onLookupWord?: (word: string, sourceLang?: string, targetLang?: string) => void;
}

const safeRenderString = (val: any): string => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'boolean') return '';
  if (typeof val === 'object') {
    if (typeof val.word === 'string') return val.word;
    if (typeof val.definition === 'string') return val.definition;
    if (typeof val.example === 'string') return val.example;
    if (typeof val.text === 'string') return val.text;
    if (typeof val.translation === 'string') return val.translation;
    try {
      return JSON.stringify(val);
    } catch {
      return '';
    }
  }
  return String(val);
};

export const UnifiedDictionaryModal: React.FC<UnifiedDictionaryModalProps> = ({
  resolvedData,
  loading,
  target,
  onClose,
  isOpenExpandedModal = false,
  onOpenExpandedModal,
  onLookupWord
}) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(isOpenExpandedModal);
  const [activeTab, setActiveTab] = useState<'definitions' | 'examples' | 'translations'>('definitions');
  const [playingExampleIndex, setPlayingExampleIndex] = useState<number | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [sentenceTranslations, setSentenceTranslations] = useState<Record<string, string>>({});
  const [translatingSentences, setTranslatingSentences] = useState<Record<string, boolean>>({});
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleTranslateSentence = async (sentence: string, sIdxKey: string) => {
    if (sentenceTranslations[sIdxKey]) {
      setSentenceTranslations(prev => {
        const copy = { ...prev };
        delete copy[sIdxKey];
        return copy;
      });
      return;
    }

    setTranslatingSentences(prev => ({ ...prev, [sIdxKey]: true }));
    try {
      const userNative = (i18n.language || (typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : 'pt') || 'pt').split('-')[0].toLowerCase();
      const sourceL = target?.sourceLang || 'en';
      const res = await translationService.unifiedTranslate(sentence, userNative, undefined, sourceL);
      if (res && res.translation && res.translation !== sentence && !res.translation.includes('indisponível')) {
        setSentenceTranslations(prev => ({ ...prev, [sIdxKey]: res.translation }));
      } else {
        window.dispatchEvent(new CustomEvent('translation_offline_prompt'));
      }
    } catch (err) {
      window.dispatchEvent(new CustomEvent('translation_offline_prompt'));
    } finally {
      setTranslatingSentences(prev => ({ ...prev, [sIdxKey]: false }));
    }
  };

  const renderInteractiveText = (text: string, srcLang?: string, tgtLang?: string) => {
    const parts = text.split(/(\s+)/);
    return parts.map((part, pIdx) => {
      const isWord = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (isWord) {
        return (
          <span
            key={pIdx}
            onClick={(e) => {
              e.stopPropagation();
              const clean = part.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
              if (clean && onLookupWord) {
                onLookupWord(clean, srcLang, tgtLang);
              }
            }}
            className="hover:text-tintd-sky hover:underline cursor-pointer transition-colors"
          >
            {part}
          </span>
        );
      }
      return <span key={pIdx}>{part}</span>;
    });
  };

  useEffect(() => {
    setIsExpanded(isOpenExpandedModal);
  }, [isOpenExpandedModal]);

  // Stop audio whenever active tab changes or modal expands/closes
  useEffect(() => {
    textToSpeechService.stop();
    setPlayingExampleIndex(null);
  }, [activeTab, isExpanded]);

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        const targetEl = e.target as HTMLElement;
        if (!targetEl.closest('.unified-dictionary-container') && !targetEl.closest('.unified-dictionary-trigger')) {
          textToSpeechService.stop();
          onClose();
        }
      }
    };
    if (target && !isExpanded) {
      window.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('touchstart', handleClickOutside);
    };
  }, [target, isExpanded, onClose]);

  // Viewport Bounds Calculation for Popover Position
  useEffect(() => {
    if (!target || isExpanded) return;

    const computePosition = () => {
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      const popW = Math.min(320, screenW - 32);
      const popH = 140;

      let rawX = target.x ?? screenW / 2;
      let rawY = target.y ?? screenH / 2;

      let left = Math.max(16, Math.min(rawX - popW / 2, screenW - popW - 16));
      let top = rawY - popH - 12;

      if (top < 16) {
        top = rawY + 28;
      }

      if (top + popH > screenH - 16) {
        top = Math.max(16, screenH - popH - 16);
      }

      setPopoverPos({ left, top });
    };

    computePosition();
    window.addEventListener('resize', computePosition);
    return () => window.removeEventListener('resize', computePosition);
  }, [target, isExpanded]);

  const richItem = resolvedData?.richItem;

  // Group definitions by POS, sorting formal words first, then informal words (with 'informal' tag and bugged [['informal']] removed)
  const groupedDefinitions = useMemo(() => {
    if (!richItem?.definitions || !Array.isArray(richItem.definitions) || richItem.definitions.length === 0) return [];
    const groupsMap = new Map<string, { formal: string[]; informal: string[] }>();

    for (const def of richItem.definitions) {
      if (!def) continue;
      let posKey = 'general';
      let text = '';
      let ex = '';

      if (typeof def === 'string') {
        text = def;
      } else if (typeof def === 'object') {
        posKey = safeRenderString(def.pos || 'general').toLowerCase().trim() || 'general';
        text = safeRenderString(def.definition || def.word || def.text || '').trim();
        ex = safeRenderString(def.example || '');
      }

      text = text.replace(/\[\[.*?\]\]/g, '').replace(/"\[\[.*?\]\]"/g, '').replace(/'\[\[.*?\]\]'/g, '').trim();
      if (!text) continue;

      if (!groupsMap.has(posKey)) {
        groupsMap.set(posKey, { formal: [], informal: [] });
      }
      const group = groupsMap.get(posKey)!;

      const isInformal = ex.toLowerCase().includes('informal') || text.toLowerCase().includes('informal') || /\[\[.*informal.*\]\]/i.test(ex);

      if (isInformal) {
        if (!group.informal.includes(text)) {
          group.informal.push(text);
        }
      } else {
        if (!group.formal.includes(text)) {
          group.formal.push(text);
        }
      }
    }

    const result: { pos: string; formal: string[]; informal: string[] }[] = [];
    groupsMap.forEach((val, pos) => {
      result.push({ pos, formal: val.formal, informal: val.informal });
    });
    return result;
  }, [richItem?.definitions]);

  if (!target) return null;

  const wordToSpeak = safeRenderString(resolvedData?.word || target.word);
  const isoLang = target.sourceLang || 'en';

  const handleSpeak = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (wordToSpeak) {
      textToSpeechService.stop();
      setPlayingExampleIndex(null);
      textToSpeechService.speak(wordToSpeak, isoLang);
    }
  };

  const handleToggleExampleTts = (exampleText: string, idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (playingExampleIndex === idx) {
      textToSpeechService.stop();
      setPlayingExampleIndex(null);
    } else {
      textToSpeechService.stop();
      setPlayingExampleIndex(idx);
      textToSpeechService.speak(exampleText, isoLang).finally(() => {
        setPlayingExampleIndex(prev => (prev === idx ? null : prev));
      });
    }
  };

  const handleShowDictionaryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    textToSpeechService.stop();
    setPlayingExampleIndex(null);
    if (onOpenExpandedModal) {
      onOpenExpandedModal();
    } else {
      setIsExpanded(true);
    }
  };

  // Render Frequency Bars (1, 2, or 3 blue bars)
  const renderFrequencyBars = (count: number = 3) => {
    const barsCount = Math.max(1, Math.min(3, Math.round(count)));
    return (
      <div className="flex items-center gap-1" title={`${t('dictionary.frequency', 'Frequência')}: ${barsCount}/3`}>
        {[1, 2, 3].map((b) => (
          <div
            key={b}
            className={`w-1.5 h-4 rounded-sm transition-all ${
              b <= barsCount ? 'bg-tintd-sky' : 'bg-tint-stone '
            }`}
          />
        ))}
      </div>
    );
  };

  // --- STATE 2: Expanded Full Dictionary Modal ---
  if (isExpanded) {
    const examplesList = richItem?.examples && Array.isArray(richItem.examples) ? richItem.examples : [];
    const posDictionaryGroup = richItem?.dictionary && Array.isArray(richItem.dictionary) ? richItem.dictionary : [];

    return (
      <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/30 backdrop-blur-md animate-in fade-in duration-200">
        <style>{`
          .custom-dict-scrollbar::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          .custom-dict-scrollbar::-webkit-scrollbar-track {
            background: rgba(30, 41, 59, 0.5);
            border-radius: 9999px;
          }
          .custom-dict-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(71, 85, 105, 0.5);
            border-radius: 9999px;
          }
          .custom-dict-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.7);
          }
        `}</style>
        <div
          ref={popoverRef}
          className="bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-3xl w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200 unified-dictionary-container relative z-10"
        >
          {/* Top Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06] dark:border-white/[0.06] bg-surface/90">
            <div className="flex items-center gap-3">
              <h3 className="text-xl sm:text-2xl font-medium tracking-[-0.03em] text-ink capitalize">{wordToSpeak}</h3>
              <button
                onClick={handleSpeak}
                className="p-2 rounded-xl bg-tintd-sky/10 hover:bg-tintd-sky/20 text-tintd-sky hover:text-tintd-sky transition-all border border-tintd-sky/20 active:scale-95 cursor-pointer"
                title={t('dictionary.listen', 'Ouvir pronúncia')}
              >
                <Volume2 size={20} />
              </button>
            </div>
            <button
              onClick={() => {
                textToSpeechService.stop();
                setPlayingExampleIndex(null);
                onClose();
              }}
              className="p-2.5 rounded-xl bg-tint-stone hover:bg-tint-stone text-muted hover:text-ink transition-all border border-black/[0.06] dark:border-white/[0.06] active:scale-95 cursor-pointer"
              title={t('common.close', 'Fechar')}
            >
              <X size={18} />
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center justify-between border-b border-black/[0.06] dark:border-white/[0.06] px-2 sm:px-4 bg-surface/40">
            <button
              onClick={() => setActiveTab('definitions')}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-3 text-[11px] sm:text-sm font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'definitions'
                  ? 'border-tintd-sky text-tintd-sky'
                  : 'border-transparent text-muted hover:text-ink'
              }`}
            >
              <BookOpen size={14} className="sm:w-4 sm:h-4 shrink-0" />
              <span>{t('dictionary.definitions', 'Definições')}</span>
            </button>
            <button
              onClick={() => setActiveTab('examples')}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-3 text-[11px] sm:text-sm font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'examples'
                  ? 'border-tintd-sky text-tintd-sky'
                  : 'border-transparent text-muted hover:text-ink'
              }`}
            >
              <List size={14} className="sm:w-4 sm:h-4 shrink-0" />
              <span>{t('dictionary.examples', 'Exemplos')}</span>
            </button>
            <button
              onClick={() => setActiveTab('translations')}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-3 text-[11px] sm:text-sm font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'translations'
                  ? 'border-tintd-sky text-tintd-sky'
                  : 'border-transparent text-muted hover:text-ink'
              }`}
            >
              <RefreshCw size={14} className="sm:w-4 sm:h-4 shrink-0" />
              <span>{t('dictionary.translations', 'Traduções')}</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6 overflow-y-auto space-y-4 flex-1 custom-dict-scrollbar">
            {/* Tab 1: Definições (Grouped by POS, formal first, informal at end) */}
            {activeTab === 'definitions' && (
              <div className="space-y-4">
                {groupedDefinitions.length > 0 ? (
                  groupedDefinitions.map((group, idx) => {
                    const allWordsList: { text: string; isInformal: boolean }[] = [
                      ...group.formal.map(w => ({ text: w, isInformal: false })),
                      ...group.informal.map(w => ({ text: w, isInformal: true }))
                    ];

                    const combinedText = allWordsList.map(w => w.text).join(', ');
                    const defKey = `def_${idx}`;

                    return (
                      <div key={idx} className="p-4 rounded-2xl bg-surface/50 border border-black/[0.06] dark:border-white/[0.06] space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-tintd-sky/10 text-tintd-sky border border-tintd-sky/20">
                            {safeRenderString(group.pos)}
                          </span>
                          <button
                            onClick={() => handleTranslateSentence(combinedText, defKey)}
                            disabled={translatingSentences[defKey]}
                            className="opacity-50 hover:opacity-100 p-1.5 rounded-lg hover:bg-surface/50 text-muted hover:text-tintd-sky transition-all cursor-pointer"
                            title="Traduzir para o idioma nativo"
                          >
                            {translatingSentences[defKey] ? (
                              <Loader2 size={13} className="animate-spin text-tintd-sky" />
                            ) : (
                              <Languages size={14} />
                            )}
                          </button>
                        </div>
                        <div className="text-sm text-ink font-medium leading-relaxed pt-1">
                          {allWordsList.map((item, wIdx) => (
                            <span key={wIdx}>
                              <span className="font-semibold">{renderInteractiveText(safeRenderString(item.text), target?.sourceLang || 'en', target?.targetLang || 'pt')}</span>
                              {item.isInformal && <span className="text-xs text-muted font-normal italic"> (informal)</span>}
                              {wIdx < allWordsList.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                        {sentenceTranslations[defKey] && (
                          <p className="text-xs text-tintd-sky/90 italic font-medium pt-1.5 border-t border-black/[0.06] dark:border-white/[0.06]">
                            {sentenceTranslations[defKey]}
                          </p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-5 rounded-2xl bg-surface/40 border border-black/[0.06] dark:border-white/[0.06] text-center space-y-2">
                    <p className="text-base font-bold text-ink">{safeRenderString(resolvedData?.primaryTranslation)}</p>
                    <p className="text-xs text-muted">
                      {t('dictionary.noDefinitions', 'Sem definições gramaticais expandidas para esta palavra.')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 2: Exemplos (with TTS play/stop button on right) */}
            {activeTab === 'examples' && (
              <div className="space-y-3">
                {examplesList.length > 0 ? (
                  examplesList.map((ex, idx) => {
                    const exText = safeRenderString(ex);
                    if (!exText) return null;
                    const exKey = `ex_${idx}`;
                    return (
                      <div key={idx} className="p-3.5 rounded-2xl bg-surface/40 border border-black/[0.06] dark:border-white/[0.06] space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="w-2 h-2 rounded-full bg-tintd-sky mt-2 shrink-0" />
                            <p className="text-sm text-ink font-medium leading-relaxed">
                              {renderInteractiveText(exText, target?.sourceLang || 'en', target?.targetLang || 'pt')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={() => handleTranslateSentence(exText, exKey)}
                              disabled={translatingSentences[exKey]}
                              className="opacity-50 hover:opacity-100 p-2 rounded-xl border border-black/[0.06] dark:border-white/[0.06] hover:bg-surface/50 text-muted hover:text-tintd-sky transition-all cursor-pointer active:scale-95"
                              title="Traduzir frase para o idioma nativo"
                            >
                              {translatingSentences[exKey] ? (
                                <Loader2 size={14} className="animate-spin text-tintd-sky" />
                              ) : (
                                <Languages size={15} />
                              )}
                            </button>
                            <button
                              onClick={(e) => handleToggleExampleTts(exText, idx, e)}
                              className={`p-2 rounded-xl border transition-all shrink-0 cursor-pointer active:scale-95 ${
                                playingExampleIndex === idx
                                  ? 'bg-bad/20 border-bad/40 text-bad hover:bg-bad/30'
                                  : 'bg-tintd-sky/10 border-tintd-sky/20 text-tintd-sky hover:bg-tintd-sky/20'
                              }`}
                              title={playingExampleIndex === idx ? t('dictionary.stop', 'Parar') : t('dictionary.listen', 'Ouvir')}
                            >
                              {playingExampleIndex === idx ? <Square size={14} className="fill-current" /> : <Volume2 size={16} />}
                            </button>
                          </div>
                        </div>
                        {sentenceTranslations[exKey] && (
                          <p className="text-xs text-tintd-sky/90 italic font-medium pl-5 border-t border-black/[0.06] dark:border-white/[0.06] pt-1.5">
                            {sentenceTranslations[exKey]}
                          </p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-5 rounded-2xl bg-surface/40 border border-black/[0.06] dark:border-white/[0.06] text-center">
                    <p className="text-xs text-muted">
                      {t('dictionary.noExamples', 'Sem frases de exemplo adicionais para esta palavra.')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: Traduções / Dicionário Reverso */}
            {activeTab === 'translations' && (
              <div className="space-y-4">
                {posDictionaryGroup.length > 0 ? (
                  posDictionaryGroup.map((group, gIdx) => (
                    <div key={gIdx} className="space-y-2">
                      <span className="text-[11px] font-normal uppercase font-mono tracking-[0.14em] text-muted block px-1">
                        {safeRenderString(group.pos)}
                      </span>
                      <div className="rounded-2xl border border-black/[0.06] dark:border-white/[0.06] overflow-hidden divide-y divide-black/[0.06] dark:divide-white/[0.06] bg-surface/30">
                        {Array.isArray(group.translations) && group.translations.map((item, tIdx) => {
                          const wordText = safeRenderString(item?.word || item);
                          const revList = Array.isArray(item?.reverse_translations)
                            ? item.reverse_translations.map(r => safeRenderString(r)).filter(Boolean)
                            : [];
                          return (
                            <div key={tIdx} className="p-3 sm:p-4 flex items-center justify-between gap-4">
                              <div className="space-y-1">
                                <span className="font-bold text-sm text-ink block capitalize">
                                  {renderInteractiveText(wordText, target?.targetLang || 'pt', target?.sourceLang || 'en')}
                                </span>
                                {revList.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 pt-1">
                                    {revList.map((rev, rIdx) => (
                                      <span
                                        key={rIdx}
                                        className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-tint-stone text-ink border border-black/[0.06] dark:border-white/[0.06]"
                                      >
                                        {renderInteractiveText(rev, target?.sourceLang || 'en', target?.targetLang || 'pt')}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="shrink-0">
                                {renderFrequencyBars(typeof item?.frequency_bars === 'number' ? item.frequency_bars : 3)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-black/[0.06] dark:border-white/[0.06] bg-surface/30 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm text-ink capitalize">
                        {renderInteractiveText(safeRenderString(resolvedData?.primaryTranslation), target?.targetLang || 'pt', target?.sourceLang || 'en')}
                      </span>
                      {renderFrequencyBars(3)}
                    </div>
                    {resolvedData?.translations && Array.isArray(resolvedData.translations) && (
                      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-black/[0.06] dark:border-white/[0.06]">
                        {resolvedData.translations.map((tItem, tIdx) => (
                          <span
                            key={tIdx}
                            className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-tint-stone text-ink border border-black/[0.06] dark:border-white/[0.06]"
                          >
                            {renderInteractiveText(safeRenderString(tItem), target?.targetLang || 'pt', target?.sourceLang || 'en')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- STATE 1: Floating Balloon (Tooltip / Popover) ---
  const synonymsText = resolvedData?.translations && Array.isArray(resolvedData.translations)
    ? resolvedData.translations.map(s => safeRenderString(s)).filter(Boolean).join(', ')
    : '';

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: `${popoverPos.left}px`,
        top: `${popoverPos.top}px`,
        zIndex: 999999
      }}
      className="bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-2xl p-4 w-[280px] sm:w-[320px] animate-in fade-in zoom-in-95 duration-150 unified-dictionary-container"
    >
      {/* Top: Original word + TTS speaker button */}
      <div className="flex items-center justify-between pb-2 border-b border-black/[0.06] dark:border-white/[0.06] mb-2">
        <span className="font-semibold text-xs text-muted capitalize truncate max-w-[190px]">
          {wordToSpeak}
        </span>
        <button
          onClick={handleSpeak}
          className="p-1.5 rounded-lg bg-tintd-sky/10 hover:bg-tintd-sky/20 text-tintd-sky hover:text-tintd-sky transition-all border border-tintd-sky/20 active:scale-95 cursor-pointer"
          title={t('dictionary.listen', 'Ouvir')}
        >
          <Volume2 size={14} />
        </button>
      </div>

      {/* Center (Highlight): primary_translation */}
      <div className="py-1">
        {loading ? (
          <div className="flex items-center gap-2 text-muted text-sm py-1">
            <span className="w-2 h-2 rounded-full bg-tintd-sky animate-ping" />
            <span className="text-xs font-medium">{t('common.loading', 'Traduzindo...')}</span>
          </div>
        ) : (
          <h4 className="text-lg sm:text-xl font-semibold text-ink leading-tight capitalize">
            {safeRenderString(resolvedData?.primaryTranslation || wordToSpeak)}
          </h4>
        )}
      </div>

      {/* Subtitle: Synonyms / variations separated by comma */}
      {synonymsText && !loading && (
        <p className="text-xs text-muted truncate pt-1 font-medium">
          {synonymsText}
        </p>
      )}

      {/* Bottom Right: "mostrar dicionário" action with 70% opacity */}
      <div className="flex justify-end pt-3 mt-2 border-t border-black/[0.06] dark:border-white/[0.06]">
        <button
          onClick={handleShowDictionaryClick}
          className="text-[11px] font-bold text-tintd-sky hover:text-tintd-sky cursor-pointer transition-all hover:underline"
          style={{ opacity: 0.7 }}
        >
          {t('dictionary.show', 'mostrar dicionário')} →
        </button>
      </div>
    </div>
  );
};

export default UnifiedDictionaryModal;


